// Slider.js
// 0.8.0
// 2020-10-12
// Felix Kiesewetter <info@felixkiesewetter.de>

class SwipeGesture {

    constructor(node=null, options={})
    {
        this.node = node;
        this.setSettings(options);
        this.reset();
        this.init();
    }

    init()
    {
        this.settings.mouse ? this.node.addEventListener('mousedown', (e) => this.start(e), false) : null;
        this.node.addEventListener('touchstart', (e) => this.start(e), false);
        this.settings.mouse ? this.node.addEventListener('mousemove', (e) => this.move(e), false) : null;
        this.node.addEventListener('touchmove', (e) => this.move(e), false);
        this.settings.mouse ? this.node.addEventListener('mouseup', (e) => this.release(e), false) : null;
        this.node.addEventListener('touchend', (e) => this.release(e), false);
    }

    start(e)
    {
        if (this.inProgress) {
            return false;
        }
        this.inProgress = true;
        this.startX = e.pageX || e.originalEvent.touches[0].pageX;
        this.interval = setInterval(() => {
                this.duration += 10;
            },10
        );
        if (typeof this.settings.onStart === 'function') {
            this.settings.onStart();
        }
    }

    move(e)
    {
        if (!this.inProgress) {
            return;
        }
        this.currentX = e.pageX || e.originalEvent.touches[0].pageX;
        this.distance = Math.abs(this.currentX - this.startX);
        this.direction = (this.currentX - this.startX) < 0 ? 1 : -1;
        if (typeof this.settings.onMove === 'function') {
            this.settings.onMove({
                distance: this.distance,
                duration: this.duration,
                direction: this.direction
            });
        }
    }

    release(e) {
        if (!this.inProgress) {
            console.log('ERROR CATCH');
            return;
        }
        let fire = false;
        const speed = this.distance / this.duration;
        if (speed > this.settings.speedThreshold) {
            fire = true;
        }
        if (!fire && this.distance >= 1) {
            console.log(this.node.offsetWidth);
            console.log(this.settings.distanceThreshold);
            console.log(this.node.offsetWidth * this.settings.distanceThreshold);
            if (this.distance >= this.node.offsetWidth * this.settings.distanceThreshold) {
                fire = true;
            }
        }
        if (!fire) {
            this.timeout = setTimeout(this.cancel, 1000);
        }
        if (typeof this.settings.onRelease === 'function') {
            this.settings.onRelease({
                distance: this.distance,
                direction: this.direction,
                fire: fire,
                speed: speed || 0
            });
        }
        this.reset();
    }

    reset()
    {
        this.inProgress = false;
        this.startX = 0;
        this.currentX = 0;
        this.distance = 0;
        this.duration = 0;
        clearTimeout(this.timeout);
        clearInterval(this.interval);
    }

    setSettings(options)
    {
        const defaultSettings = {
            mouse: true,
            speedThreshold: 1,
            distanceThreshold: .5,
            onStart: () => {
                // do stuff
            },
            onMove: (params) => {
                // do stuff
            },
            onRelease: (params) => {
                // do stuff
            },
        };
        this.settings = {...defaultSettings, ...options};
    }

}

export default class Slider
{

    /**
     * Class Constructor
     * @param {object} node
     * @param {object} options
     * @returns {void}
     */
    constructor(node, options)
    {
        this.node = node;
        this.errors = [];
        if (!this.node) {
            this.errors.push(`Slider expects first parameter to be a DOM element but variable node is of type ${typeof node}`);
        }
        else {
            this.setSettings(options);
            this.items = this.node.querySelectorAll(this.settings.itemSelector);
            if (!this.items.length) {
                this.errors.push(`Slider needs at least one child with selector "${this.settings.itemSelector}"`);
            }
            else {
                this.init();
            }
        }
        this.debug();
    }

    /**
     * Initialise Slider
     * @returns {void}
     */
    init()
    {
        this.currentIndex = 0;
        this.currentTranslateX = 0;
        this.node.classList.add(`slider`);
        this.node.classList.add(`slider--mode-${this.settings.mode}`);
        this.wrapper = this.getWrapper() || null;
        this.navigation = this.getNavigation();
        this.navigationItems = this.getNavigationItems();
        this.controls = this.getControls();
        this.autoplayIndicator = this.getAutoplayIndicator();
        for (const item of this.items) item.classList.add(`${this.settings.itemSelector.substring(1)}`);
        this.implementKeyboardControls();
        this.resize(0);
        window.addEventListener('resize', () => { this.resize(); });
        this.initAutoplay();
        this.initGestures();
    }

    /**
     * Initialise Autoplay
     * @returns {void}
     */
    initAutoplay()
    {
        if (this.settings.slideContinously) {
            if (!this.settings.autoplay) {
                this.settings.autoplay = true;
                console.warn('Autoplay is disabled, but required if \'slideContinously\' is enabled');
            }
            this.settings.autoplayInterval = 10;
        }
        else if (!this.settings.autoplay) {
            return;
        }
        this.debug('initAutoplay');
        this.autoplayInterval = setInterval(() => {
            this.runAutoplay();
        }, this.settings.autoplayInterval);
        this.settings.autoplayStopEvents.split(',').forEach((eventType) => {
            eventType = eventType.trim();
            this.node.addEventListener(eventType, () => {
                if (null !== this.autoplayInterval) {
                    clearInterval(this.autoplayInterval);
                    this.autoplayInterval = null;
                    this.autoplayIndicator.classList.add(`${this.settings.autoplayIndicatorSelector.substring(1)}--hold`);
                }
            });
        });
        this.settings.autoplayResumeEvents.split(',').forEach((eventType) => {
            eventType = eventType.trim();
            this.node.addEventListener(eventType, () => {
                if (null === this.autoplayInterval) {
                    this.autoplayInterval = setInterval(() => {
                        this.runAutoplay();
                    }, this.settings.autoplayInterval);
                    this.autoplayIndicator.classList.remove(`${this.settings.autoplayIndicatorSelector.substring(1)}--hold`);
                }
            });
        });
    }

    /**
     * Initialise Gestures
     * @returns {void}
     */
    initGestures()
    {
        if (!this.settings.gestures) {
            return;
        }
        this.node.classList.add('slider--implements-gestures');
        new SwipeGesture(this.node, {
            mouse: this.settings.gesturesWithMouse,
            distanceThreshold: this.settings.itemsPerView !== 'auto' ? 1 / this.settings.itemsPerView : .25,
            onStart: () => {
                this.node.classList.add('slider--hold-transition');
            },
            onMove: (params) => {
                this.wrapper.style.setProperty('--dragX', `${-1 * params.direction * params.distance}px`)
            },
            onRelease: (params) => {
                this.node.classList.remove('slider--hold-transition');
                this.wrapper.style.setProperty('--dragX', '0px');
                if (params.fire) {
                    this.debug('Swipe:');
                    this.debug(params);
                    params.direction > 0 ? this.slideTo(this.getNextIndex()) : this.slideTo(this.getPrevIndex());
                }
            }
        });
    }

    /**
     * Run Autoplay
     * @returns {void}
     */
    runAutoplay()
    {
        if (this.settings.slideContinously) {
            this.slideBy(1);
        }
        else {
            this.slideTo(this.getNextIndex());
            this.debug('runAutoplay');
        }
    }

    /**
     * Get Wrapper
     * Either gets wrapper by selector or builds it and returns element
     * @returns {null|HTMLDivElement}
     */
    getWrapper()
    {
        if (this.settings.mode === 'slide') {
            let wrapper = this.node.querySelector(this.settings.wrapperSelector);
            if (!wrapper) {
                wrapper = document.createElement('div');
                wrapper.classList.add(this.settings.wrapperSelector.substring(1));
                for (const item of this.items) {
                    wrapper.appendChild(item);
                }
                this.node.appendChild(wrapper);
            }
            if (this.settings.mode === 'slide') {
                if (!this.settings.slideContinously) {
                    wrapper.style.transition = `transform ${this.settings.transitionDuration}ms ${this.settings.transitionEasing}`;
                }
                wrapper.style.transform = 'translateX(calc(var(--dragX) + var(--slideX)))';
                wrapper.style.setProperty('--dragX', '0px');
                wrapper.style.setProperty('--slideX', '0px');
            }
            return wrapper;
        }
    }

    /**
     * Get Navigation
     * If navigation is being used, either gets navigation by selector or builds it and returns element
     * If navigation is not being used, returns null
     * @returns {null|HTMLUListElement}
     */
    getNavigation()
    {
        if (this.settings.navigation === true) {
            let navigation = this.node.querySelector(this.settings.navigationSelector);
            if (!navigation) {
                navigation = document.createElement('ul');
                navigation.classList.add(this.settings.navigationSelector.substring(1));
                let index = 1;
                for (const item of this.items) {
                    let navigationItem = document.createElement('li');
                    navigationItem.innerHTML = `<span>${index}</span>`;
                    navigation.appendChild(navigationItem);
                    index ++;
                }
                this.node.appendChild(navigation);
            }
            return navigation;
        }
        return null;
    }

    /**
     * Get Controls
     * If controls are being used, either gets controls by selectors or builds them and returns object with prev and next controls
     * If controls are not being used, returns null
     * @returns {null|object}
     */
    getControls()
    {
        if (this.settings.controls === true) {
            let prev = this.node.querySelector(`${this.settings.controlsSelector}--${this.settings.controlsPrevModifier}`);
            if (!prev) {
                prev = document.createElement('button');
                prev.textContent = '<';
                prev.classList.add(`${this.settings.controlsSelector.substring(1)}`);
                prev.classList.add(`${this.settings.controlsSelector.substring(1)}--${this.settings.controlsPrevModifier}`);
                prev.addEventListener('click', () => {
                    this.slideTo(this.getPrevIndex());
                });
                this.node.appendChild(prev);
            }
            let next = this.node.querySelector(`${this.settings.controlsSelector}--${this.settings.controlsNextModifier}`);
            if (!next) {
                next = document.createElement('button');
                next.textContent = '>';
                next.classList.add(`${this.settings.controlsSelector.substring(1)}`);
                next.classList.add(`${this.settings.controlsSelector.substring(1)}--${this.settings.controlsNextModifier}`);
                next.addEventListener('click', () => {
                    this.slideTo(this.getNextIndex());
                });
                this.node.appendChild(next);
            }
            return {
                0: prev,
                1: next
            };
        }
        return null;
    }

    /**
     * Get Autoplay Indicator
     * If autplay is being used, either gets autoplay indicator by selector or builds it and returns element
     * If autoplay is not being used, returns null
     * @returns {null|HTMLDivElement}
     */
    getAutoplayIndicator()
    {
        if (this.settings.autoplay === true) {
            let autoplayIndicator = this.node.querySelector(this.settings.autoplayIndicatorSelector);
            if (!autoplayIndicator) {
                autoplayIndicator = document.createElement('div');
                autoplayIndicator.classList.add(this.settings.autoplayIndicatorSelector.substring(1));
                autoplayIndicator.style.setProperty('--d', `${this.settings.autoplayInterval}ms`);
                this.node.appendChild(autoplayIndicator);
            }
            return autoplayIndicator;
        }
        return null;
    }

    /**
     * Get Previous Index
     * @returns {number}
     */
    getPrevIndex()
    {
        if (this.directionLock === -1) {
            return this.currentIndex;
        }
        const itemsPerView = parseInt(this.settings.itemsPerView) || 1;
        switch (this.settings.slideMode) {
            case 'strict':
                return Math.max(0, Math.min(this.items.length - itemsPerView, this.currentIndex - 1));
            case 'rotate':
                return this.currentIndex - 1 >= 0 ? this.currentIndex - 1 : this.items.length - itemsPerView;
            case 'endless':
                return this.currentIndex - 1;
        }
    }

    /**
     * Get Next Index
     * @returns {number}
     */
    getNextIndex()
    {
        if (this.directionLock === 1) {
            return this.currentIndex;
        }
        const itemsPerView = parseInt(this.settings.itemsPerView) || 1;
        switch (this.settings.slideMode) {
            case 'strict':
                return Math.max(0, Math.min(this.items.length - itemsPerView, this.currentIndex + 1));
            case 'rotate':
                return this.currentIndex + 1 > this.items.length - itemsPerView ? 0 : this.currentIndex + 1;
            case 'endless':
                return this.currentIndex + 1;
        }
    }

    /**
     * Get Navigation Items
     * @returns {null|object}
     */
    getNavigationItems()
    {
        if (null !== this.navigation) {
            const navigationItems = this.navigation.querySelectorAll('li');
            Object.keys(navigationItems).forEach((index) => {
                navigationItems[index].classList.add('slider__navigation__item');
                navigationItems[index].addEventListener('click', () => {
                    this.slideTo(index);
                });
            });
            return navigationItems;
        }
        return null;
    }

    /**
     * Get Settings From Data Attributes
     * @returns {object}
     */
    getSettingsFromDataAttributes()
    {
        let settingsFromDataAttributes = {};
        if (this.node.getAttribute('data-items-per-view')) {
            settingsFromDataAttributes.itemsPerView = parseInt(this.node.getAttribute('data-items-per-view'));
        }
        if (this.node.getAttribute('data-navigation')) {
            settingsFromDataAttributes.navigation = this.node.getAttribute('data-navigation') === '1' ? true : false;
        }
        if (this.node.getAttribute('data-controls')) {
            settingsFromDataAttributes.controls = this.node.getAttribute('data-controls') === '1' ? true : false;
        }
        return settingsFromDataAttributes;
    }

    /**
     * Set Settings
     * Combines default settings with settings form data attributes and options parameter
     * Sets this.settings variables
     * @param {object} options
     * @returns {void}
     */
    setSettings(options)
    {
        const defaultSettings = {
            debug: false,
            mode: 'slide', // or 'fade'
            slideContinously: false,
            slideMode: 'strict', // or 'rotate' or 'endless'
            autoplay: false,
            autoplayInterval: 2500,
            autoplayIndicator: true,
            autoplayIndicatorSelector: '.slider__autoplay-indicator',
            autoplayStopEvents: 'click, mouseenter',
            autoplayResumeEvents: 'mouseleave',
            itemSelector: '.slider__item',
            itemsPerView: 1, // or 'auto'
            itemsCurrentModifier: 'current',
            controls: false,
            controlsSelector: '.slider__control',
            controlsPrevModifier: 'previous',
            controlsNextModifier: 'next',
            controlsDisabledModifier: 'disabled',
            navigation: false,
            navigationSelector: '.slider__navigation',
            navigationItemSelector: '.slider__navigation__item',
            navigationItemCurrentModifier: 'current',
            transitionDuration: 500,
            transitionEasing: 'ease-in-out',
            wrapperSelector: '.slider__wrapper',
            keyboard: true,
            gestures: true,
            gesturesWithMouse: true
        };
        const dataSettings = this.getSettingsFromDataAttributes();
        this.settings = {...defaultSettings, ...dataSettings, ...options};
    }

    /**
     * Slide To
     * Calculates new translateX value and slides the wrapper by changing its style value
     * @param {int} targetIndex
     * @returns {void}
     */
    slideTo(targetIndex=0)
    {
        this.debug(`slideTo(${targetIndex})`);
        let targetTranslateX;
        switch (this.settings.mode) {
            case 'slide':
                switch (this.settings.itemsPerView) {
                    case 'auto':
                        targetTranslateX = this.items[targetIndex].offsetLeft;
                        this.wrapper.style.setProperty('--slideX', `-${targetTranslateX}px`);
                        break;
                    default:
                        targetTranslateX = Math.floor(targetIndex * 100 / this.items.length * 10) / 10;
                        this.wrapper.style.setProperty('--slideX', `-${targetTranslateX}%`);
                        break;
                }
                break;
        }
        this.updateDirectionLock(targetIndex, targetTranslateX);
        this.updateNavigation(targetIndex);
        this.updateControls();
        this.updateItems(targetIndex);
        this.currentIndex = targetIndex;
        this.currentTranslateX = targetTranslateX || 0;
    }

    /**
     * Slide By
     * Moves the slider by given amount of pixels
     * @param {int} amount
     * @returns {void}
     */
    slideBy(amount)
    {
        this.debug(`slideBy(${amount})`);
        let translateX = this.currentTranslateX + amount;
        this.wrapper.style.setProperty('--slideX', `-${translateX}px`);
        this.currentTranslateX = translateX;


        // This is absolutely quick-and-dirty ---> fix this at some point:
        if (typeof this.cloned === 'undefined') this.cloned = false;
        if (this.currentTranslateX >= this.node.offsetWidth && !this.cloned) {
            for (let i = 0; i <= 2; i++) {
                for (const item of this.items) {
                    this.wrapper.appendChild(item.cloneNode(true));
                }
            }
            this.cloned = true;
        }
    }

    /**
     * Resize
     * @param int delay
     * @returns {void}
     */
    resize(delay=500)
    {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            if (this.settings.itemsPerView !== 'auto') {
                const wrapperWidth = (this.items.length / this.settings.itemsPerView) * 100;
                this.wrapper.style.width = `${wrapperWidth}%`;
                for (const item of this.items) {
                    item.style.width = `${wrapperWidth / this.settings.itemsPerView}%`;
                }
            }
            else {
                this.totalItemsWidth = 0;
                for (const item of this.items) {
                    this.totalItemsWidth += item.offsetWidth;
                }
            }
            this.debug('resize');
            this.slideTo(this.currentIndex);
        }, delay);
    }

    /**
     * Update Items
     * Current item gets an additional class name
     * @param {int} targetIndex
     * @returns {void}
     */
    updateItems(targetIndex=0)
    {
        const currentClassName = `${this.settings.itemSelector.substring(1)}--${this.settings.itemsCurrentModifier}`;
        Object.keys(this.items).forEach((index) => {
            parseInt(index) === parseInt(targetIndex) ? this.items[index].classList.add(currentClassName) : this.items[index].classList.remove(currentClassName);
        });
    }

    /**
     * Update Direction Lock
     * Lock sliding to the next or previous item(s)
     * @param {int} targetIndex
     * @param {int} targetTranslateX
     * @returns {void}
     */
    updateDirectionLock(targetIndex=0, targetTranslateX=0)
    {
        if (this.settings.slideMode !== 'strict') {
            return;
        }
        if (targetIndex === 0) {
            this.directionLock = -1;
        }
        else if (targetIndex === this.items.length - 1 || (this.settings.itemsPerView === 'auto' && this.totalItemsWidth - this.node.offsetWidth <= targetTranslateX)) {
            this.directionLock = 1;
        }
        else {
            this.directionLock = 0;
        }
    }

    /**
     * Update Controls
     * If slide mode is strict, buttons will disappear at slider's start/end
     * @returns {void}
     */
    updateControls()
    {
        const disabledClassName = `${this.settings.controlsSelector.substring(1)}--${this.settings.controlsDisabledModifier}`;
        if (this.settings.slideMode === 'strict' && this.controls) {
            this.directionLock === -1 ? this.controls[0].classList.add(disabledClassName) : this.controls[0].classList.remove(disabledClassName);
            this.directionLock === 1 ? this.controls[1].classList.add(disabledClassName) : this.controls[1].classList.remove(disabledClassName);
        }
    }

    /**
     * Update Navigation
     * Sets current state of navigation items, if navigation as well as its items exist
     * @param {int} targetIndex
     * @returns {void}
     */
    updateNavigation(targetIndex=0)
    {
        if (null !== this.navigationItems) {
            Object.keys(this.navigationItems).forEach((index) => {
                const navigationItem = this.navigationItems[index];
                const currentClassName = `${this.settings.navigationItemSelector.substring(1)}--${this.settings.navigationItemCurrentModifier}`;
                parseInt(index) === parseInt(targetIndex) ? navigationItem.classList.add(currentClassName) : navigationItem.classList.remove(currentClassName);
            });
        }
    }

    /**
     * Implement Keyboard Controls
     * @returns {void}
     */
    implementKeyboardControls()
    {
        if (!this.settings.keyboard) return;
        window.addEventListener('keyup', (e) => {
            if (e.keyCode === 39 || e.keyCode === 40) {
                e.preventDefault();
                this.slideTo(this.getNextIndex());
            }
            else if (e.keyCode === 37 || e.keyCode === 38) {
                e.preventDefault();
                this.slideTo(this.getPrevIndex());
            }
        });
    }

    /**
     * Debug
     * @param log
     * @returns {void}
     */
    debug(log=false)
    {
        if (this.settings.debug) {
            if (!log) {
                const log = {
                    settings: this.settings,
                    node: this.node,
                    items: this.items,
                    errors: this.errors
                };
                if (this.errors.length) {
                    console.error(log);
                }
                else {
                    console.log(log);
                }
            }
            else {
                console.log(log);
            }
        }
    }
}
